import React, { useCallback, useState } from 'react';

import { InsightSection, TransportationSectionType } from 'store/state/insightsContext';
import { ShowAt } from 'consts/breakpoints';
import { TimeToGetSection } from '../TimeToGetSection';
import { TimeToGetToRow } from '../TimeToGetToItem';
import { TimeToGetToTitle } from '../TimeToGetToTitle';
import { useLocale } from 'locale';
import TransportationAccordion from '../transportation-accordion';
import CarouselStories from 'components/view-pager-to-stories';
import { useTheme } from 'hooks/useTheme';
import { WithContentSlotSidePaddingContainer } from 'ds/components/Layout';
import WidgetWrapper from 'components/widget/common/widget-wrapper';
import { InsightType } from 'utils/entities';
import { TransportationWidgetProps } from '../index';
import withEvents from 'analytics/withEvents';
import WidgetCollapsedWrapper from '../../common/widget-collapsed-wrapper';

const TransportationWidget: React.FunctionComponent<TransportationWidgetProps> = React.memo(({
  transportationHubsData,
  accordionData,
  carouselData,
  activeAccordion,
  transitTab,
  setActiveTransportationId,
  setTransportationState,
  setHoveredStation,
  onSeeOnMapClick,
  accordionItemsExist,
  isCollapsed,
  showCarousel,
}) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const [ slideIndex, setSlideIndex ] = useState(0);

  const setActiveSection = useCallback(
    (nextAccordion) => setTransportationState({ accordion: nextAccordion }),
    [ setTransportationState ]
  );
  const setActiveTab = useCallback(
    (nextTab) => setTransportationState({ transitTab: nextTab }),
    [ setTransportationState ]
  );

  const accordion = (
    <TransportationAccordion
      sections={accordionData}
      setActiveSection={setActiveSection}
      setActiveTab={setActiveTab}
      activeSection={activeAccordion}
      activeTab={transitTab}
      setHoveredStation={setHoveredStation}
      onSeeOnMapClick={onSeeOnMapClick}
      accordionItemsExist={accordionItemsExist}
    />
  );

  const carousel = (
    <CarouselStories
      color={colors.insights.transportation}
      insights={carouselData}
      slideIndex={slideIndex}
      onSlideIndexChange={(idx) => setSlideIndex(idx)}
      onReadMoreClick={setActiveTransportationId}
    />
  );

  const transportationData = (
    <>
      <ShowAt from={3}>
        <WithContentSlotSidePaddingContainer>
          <TimeToGetToTitle />
        </WithContentSlotSidePaddingContainer>
      </ShowAt>
      <TimeToGetToRow>
        <TimeToGetSection data={transportationHubsData} />
      </TimeToGetToRow>
    </>
  );

  if (!transportationHubsData.length && !accordionData.length && !carouselData.length && !isCollapsed) return null;

  const wrapperProps = {
    targetId: InsightSection.Transportation,
    styleType: InsightType.Transportation,
    title: t('widgetTitle.transportation'),
    transportationData: !!transportationHubsData.length && transportationData,
    carousel: carouselData.length && showCarousel ? carousel : null,
  };

  const child = accordionData.length ? accordion : null;

  return isCollapsed
    ? <WidgetCollapsedWrapper {...wrapperProps} dynamicContentData={accordionData}>{child}</WidgetCollapsedWrapper>
    : <WidgetWrapper {...wrapperProps}>{child}</WidgetWrapper>;
});

const analyticsMap = {
  [TransportationSectionType.Transit]: 'transit',
  [TransportationSectionType.Bike]: 'bikes',
};

export default withEvents<TransportationWidgetProps>((sendEvent, props) => ({
  setTransportationState({ accordion: section }) {
    if (!section) return;

    sendEvent(`${props.eventCategory}_widget_section_click`, props.eventCategory, {
      event: {
        [`${props.eventCategory}_page_sections`]: 'transportation',
        'widget_sections': `transportation/${analyticsMap[section]}`,
      },
    });
  },
}))(TransportationWidget);
